import { useState, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, BarElement, Title, Tooltip } from 'chart.js';
import ChartCard from '$src/components/ChartCard';
import Layout from '$src/components/Layout';
import LocationsSummary from '$src/components/LocationsSummary';
import ListEventsCard from '$src/components/ListEventsCard';
import LocationsTableCard from '$src/components/LocationsTableCard';
import LocationDetailsDialog from '$src/components/LocationDetailsDialog';
import { EventIcon, MonitoringStationIcon, LocationIcon } from '@ring-icons';
import useStores from '$src/hooks/useStores';
import useEventsChartData from '$src/hooks/useEventsChartData';
import useEventsAndLocations from '$src/hooks/useEventsAndLocations';
import { redirectToUpsell } from '$src/utils/redirect';
import { ENTITLEMENT_NOT_FOUND } from '$src/components/LocationsTableCard/constants';
import { handleBackToRingDashboard } from '$src/utils/handleBack';

import './index.less';

ChartJS.register(CategoryScale, LinearScale, PointElement, BarElement, Title, Tooltip);

export function useRedirectOnNoEntitlements(locations) {
  if (locations !== null) {
    const locationExistsWithEntitlement = locations.some((location) => location.eligibilityReason !== ENTITLEMENT_NOT_FOUND);
    if (!locationExistsWithEntitlement) {
      redirectToUpsell();
    }
  }
}

function Dashboard() {
  const {
    locationStatusStore: { fetchLocations, locations, loading: loadingLocationStatus, hasError },
    sessionStore: { user, isMobile },
  } = useStores();
  const [locationsDataSource, setLocationDataSource] = useState(locations);
  const [locationSelected, setLocationSelected] = useState(null);
  const [locationToSeeDetails, setLocationToSeeDetails] = useState(null);
  const currentLocationOwnerIds = useMemo(() => locationSelected ? [locationSelected.ownerId] : [...new Set(locations?.map(l => l.ownerId))], [locationSelected, locations]); 
  const { loadingEventsLocations } = useEventsAndLocations(currentLocationOwnerIds, locationSelected?.locationId, locations);
  const { eventsChartData, emergenciesChartData, isLoadingEventsChartData, isLoadingEmergencyChartData } = useEventsChartData(
    currentLocationOwnerIds,
    locationSelected?.locationId,
  );
  const eventsChartColor = '#4aba5e';
  const emergenciesChartColor = '#f5655b';

  useRedirectOnNoEntitlements(locations);

  if (hasError) {
    handleBackToRingDashboard(isMobile);
    return null;
  }

  if (user && locations === null && !loadingLocationStatus) fetchLocations(user?.user_id);

  if (locationSelected && locationsDataSource && !locationsDataSource?.find((location) => location.locationId === locationSelected.locationId)) {
    setLocationSelected();
  }


  return (
    <Layout hideBack>
      <div className="dashboard" data-testid="dashboard-page">
        <div className="dashboard__row">
          <LocationsSummary
            onChangeFilter={(locationsFiltered) => {
              setLocationDataSource(locationsFiltered);
            }}
          />
          <LocationsTableCard
            setLocationSelected={setLocationSelected}
            locationIdSelected={[locationSelected?.locationId]}
            title="Locations"
            Icon={LocationIcon}
            locations={locationsDataSource || locations}
            isLoading={loadingLocationStatus}
            onLocationDetailsClicked={(location) => setLocationToSeeDetails(location)}
            isSelectionDisabled={loadingEventsLocations || isLoadingEventsChartData || isLoadingEmergencyChartData}
          />
        </div>
        <div className="dashboard__row">
          <ListEventsCard locationSelected={locationSelected} locations={locations} />
          <ChartCard
            data-testid="events-chart"
            title={`Total Events ${locationSelected ? ` - ${locationSelected.locationName}` : ''}`}
            Icon={EventIcon}
            data={eventsChartData}
            total={eventsChartData?.count}
            success
            styles={{
              borderColor: eventsChartColor,
              backgroundColor: eventsChartColor,
            }}
            loading={isLoadingEventsChartData}
          />
          <ChartCard
            data-testid="emergency-chart"
            title={`Total Emergencies ${locationSelected ? ` - ${locationSelected.locationName}` : ''}`}
            Icon={MonitoringStationIcon}
            data={emergenciesChartData}
            total={emergenciesChartData?.count}
            danger
            styles={{
              borderColor: emergenciesChartColor,
              backgroundColor: emergenciesChartColor,
            }}
            loading={isLoadingEmergencyChartData}
          />
        </div>
      </div>
      <LocationDetailsDialog
        showModal={!!locationToSeeDetails}
        location={locationToSeeDetails}
        toggleLocationDetailsDialog={() => setLocationToSeeDetails(null)}
      />
    </Layout>
  );
}

export default observer(Dashboard);
